import * as XLSX from "xlsx";
import { ymdParts, ddMmmYy } from "./format";

// Excel serial (days since 1899-12-30) computed from pure Y/M/D via Date.UTC on both sides,
// so it is completely timezone-independent.
const _EXCEL_EPOCH = Date.UTC(1899, 11, 30);
function _excelSerial(p) {
  return Math.round((Date.UTC(p.y, p.m - 1, p.d) - _EXCEL_EPOCH) / 86400000);
}

// Returns a DATE MARKER for spreadsheet cells so exportRowsToXlsx writes a real Excel date
// cell (native sort/filter) formatted as dd-mmm-yy. Empty string when there's no date.
export const xlsxDate = (v) => {
  const p = ymdParts(v);
  if (!p) return "";
  return { __xlsxDate: true, serial: _excelSerial(p), display: ddMmmYy(v) };
};

const _isDateMarker = (v) => v && typeof v === "object" && v.__xlsxDate === true;

/**
 * Export tabular data to an .xlsx file (client-side, no backend round-trip).
 * @param {string} filename  e.g. "dispatch-register.xlsx"
 * @param {Array<{header:string, value:(row)=>any}>} columns  column defs
 * @param {Array<object>} rows  the data rows (already filtered/sorted as displayed)
 * @param {string} [sheetName]
 */
export function exportRowsToXlsx(filename, columns, rows, sheetName = "Export") {
  const header = columns.map((c) => c.header);
  // 2D array of raw cell values; date markers are kept as objects and materialised below.
  const body = rows.map((r) => columns.map((c) => c.value(r)));
  const displayBody = body.map((row) =>
    row.map((v) => (_isDateMarker(v) ? v.display : v == null ? "" : v)));
  const ws = XLSX.utils.aoa_to_sheet([header, ...displayBody]);
  // Replace date markers with real Excel date cells (numeric serial + dd-mmm-yy format).
  body.forEach((row, ri) => row.forEach((v, ci) => {
    if (_isDateMarker(v)) {
      const addr = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
      ws[addr] = { t: "n", v: v.serial, z: "dd-mmm-yy" };
    }
  }));
  // Reasonable auto column widths (cap at 48 chars), using the displayed text.
  ws["!cols"] = columns.map((c, i) => {
    const maxLen = Math.max(
      String(c.header).length,
      ...displayBody.map((row) => String(row[i] ?? "").length),
    );
    return { wch: Math.min(Math.max(maxLen + 2, 8), 48) };
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  const p = ymdParts(new Date());
  const stamp = `${p.y}-${String(p.m).padStart(2, "0")}-${String(p.d).padStart(2, "0")}`;
  XLSX.writeFile(wb, filename.replace(/\.xlsx$/i, "") + `-${stamp}.xlsx`);
}
